/**
 * Design Studio Page (formerly Web Presence)
 * AI-powered content generation, pages, forms, assets, and SEO management
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Globe,
  FileText,
  FormInput,
  Image,
  Search,
  Plus,
  Settings,
  Eye,
  Home,
  Edit,
  Trash2,
  ExternalLink,
  TrendingUp,
  Users,
  Clock,
  X,
  Upload,
  Check,
  Layout,
  Type,
  Square,
  Video,
  Download,
  Copy,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Save,
  Database,
  Palette,
  Code2,
  Shield,
  Activity,
  Sparkles,
  Share2,
  Mail,
  Printer,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Mic,
  Rocket,
  RotateCcw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { webPresenceApi, getPlatformApi } from '../../shared/services/platformApi';
import PublicBlockRenderer from './PublicBlockRenderer';
import {
  ContentTypeManager,
  DesignTokenEditor,
  SectionTemplateLibrary,
  SnippetLibrary,
  PermissionManager,
  ActivityLog,
  RedirectManager,
  BulkSEOEditor,
  SEOAuditPanel,
  PageEditor,
  AIDesignAssistant,
} from '../../modules/web-presence/components';
import type { Page as WPPage, AIGeneration } from '../../modules/web-presence/types';
import { normalizeAIBlocks } from '../../modules/web-presence/utils/normalizeAIBlocks';
import type {
  DesignToken,
  CodeSnippet,
  WebPresencePermission,
  ActivityLogEntry,
} from '../../modules/web-presence/types';
import { tvTokens, TvPageBar } from '../../shared/components/triveny';
import { DesignStudioDock } from '../../modules/web-presence/components/DesignStudioDock';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

type CreateTab = 'ai' | 'pages' | 'posts' | 'emails' | 'flyers' | 'sms' | 'voice_ai';
type ManageTab = 'assets' | 'forms' | 'content' | 'seo';
type SettingsTab = 'design' | 'code' | 'team' | 'activity';
type WebPresenceTab = CreateTab | ManageTab | SettingsTab;

interface PageItem {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  lastModified: string;
  views: number;
}

interface FormFieldDef {
  id: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
  label: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
}

interface FormItem {
  id: string;
  name: string;
  type: 'contact' | 'lead' | 'survey' | 'registration';
  submissions: number;
  conversionRate: number;
  lastSubmission: string;
  fields?: FormFieldDef[];
  successMessage?: string;
}

interface AssetItem {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document';
  size: string;
  url: string;
  uploadedAt: string;
}

// ============================================================================
// API DATA MAPPERS
// ============================================================================

/** Map API page response (snake_case) to UI PageItem */
function mapApiPage(apiPage: any): PageItem {
  return {
    id: apiPage.id,
    title: apiPage.title || apiPage.headline || apiPage.page_title || '',
    slug: apiPage.slug || apiPage.campaign_name || apiPage.page_slug || '',
    status: apiPage.status === 'published' ? 'published' : 'draft',
    lastModified: apiPage.updated_at ? new Date(apiPage.updated_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    views: apiPage.view_count || apiPage.visits || 0,
  };
}

/** Map API form response to UI FormItem */
function mapApiForm(apiForm: any): FormItem {
  let fields = apiForm.fields;
  if (typeof fields === 'string') { try { fields = JSON.parse(fields); } catch { fields = []; } }
  return {
    id: apiForm.id,
    name: apiForm.form_name || apiForm.name || '',
    type: apiForm.form_type || apiForm.type || 'contact',
    submissions: apiForm.submission_count || apiForm.submissions_count || apiForm.submissions || 0,
    conversionRate: apiForm.conversion_rate || apiForm.conversionRate || 0,
    lastSubmission: apiForm.last_submission_at ? new Date(apiForm.last_submission_at).toISOString().split('T')[0] : 'N/A',
    fields: Array.isArray(fields) ? fields : [],
    successMessage: apiForm.success_message || '',
  };
}

/** Map API page to the Page type used by PageEditor */
function mapApiPageToEditorPage(apiPage: any): WPPage {
  // Normalize blocks — handles both AI-generated (type) and native (blockType) formats
  let blocks = apiPage.content_blocks || [];
  if (typeof blocks === 'string') { try { blocks = JSON.parse(blocks); } catch { blocks = []; } }
  if (!Array.isArray(blocks)) blocks = [];
  // Normalize only blocks that are missing blockType (AI-generated)
  const needsNormalization = blocks.some((b: any) => b.type && !b.blockType);
  if (needsNormalization) blocks = normalizeAIBlocks(blocks, apiPage.id);

  return {
    id: apiPage.id,
    siteId: apiPage.site_id || '',
    pageTitle: apiPage.title || apiPage.page_title || '',
    pageSlug: apiPage.slug || apiPage.page_slug || '',
    pageType: apiPage.page_type || 'page',
    templateId: null,
    parentPageId: apiPage.parent_id || null,
    contentBlocks: blocks,
    metaTitle: apiPage.meta_title || null,
    metaDescription: apiPage.meta_description || null,
    featuredImageUrl: apiPage.og_image || null,
    sortOrder: apiPage.sort_order || 0,
    status: apiPage.status || 'draft',
    version: apiPage.version || 1,
    publishedAt: apiPage.published_at || null,
    createdBy: apiPage.created_by || null,
    createdAt: apiPage.created_at || new Date().toISOString(),
    updatedAt: apiPage.updated_at || new Date().toISOString(),
  };
}

// ============================================================================
// COMPONENTS
// ============================================================================

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface CreatePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (page: Omit<PageItem, 'id' | 'views' | 'lastModified'>) => void;
  type: 'page' | 'landing';
}

function CreatePageModal({ isOpen, onClose, onSubmit, type }: CreatePageModalProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ title, slug: slug || `/${title.toLowerCase().replace(/\s+/g, '-')}`, status });
    setTitle('');
    setSlug('');
    setStatus('draft');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New {type === 'landing' ? 'Landing Page' : 'Page'}</DialogTitle>
          <DialogDescription>
            Add a new {type === 'landing' ? 'landing page' : 'page'} to your website.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter page title"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 text-sm mr-1">/</span>
              <input
                type="text"
                value={slug.replace(/^\//, '')}
                onChange={(e) => setSlug('/' + e.target.value.replace(/^\//, ''))}
                placeholder={title ? title.toLowerCase().replace(/\s+/g, '-') : 'page-slug'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create {type === 'landing' ? 'Landing Page' : 'Page'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FormEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing form to edit; omit/null to create a new one. */
  form?: FormItem | null;
  onSubmit: (data: { name: string; type: FormItem['type']; fields: FormFieldDef[]; successMessage: string }) => void;
}

const DEFAULT_FORM_FIELDS: FormFieldDef[] = [
  { id: 'f_name', type: 'text', label: 'Name', name: 'name', required: true },
  { id: 'f_email', type: 'email', label: 'Email', name: 'email', required: true },
  { id: 'f_phone', type: 'phone', label: 'Phone', name: 'phone', required: false },
  { id: 'f_message', type: 'textarea', label: 'Message', name: 'message', required: false },
];

const FIELD_TYPES: Array<FormFieldDef['type']> = ['text', 'email', 'phone', 'textarea', 'select', 'checkbox'];

// Create + edit a form with a real field builder (add/edit/reorder/remove).
function FormEditorModal({ isOpen, onClose, form, onSubmit }: FormEditorModalProps) {
  const editing = !!form;
  const [name, setName] = useState('');
  const [type, setType] = useState<FormItem['type']>('contact');
  const [fields, setFields] = useState<FormFieldDef[]>(DEFAULT_FORM_FIELDS);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(form?.name || '');
    setType(form?.type || 'contact');
    setFields(form?.fields && form.fields.length ? form.fields : DEFAULT_FORM_FIELDS);
    setSuccessMessage(form?.successMessage || '');
  }, [isOpen, form]);

  const slug = (s: string) => (s || 'field').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const updateField = (i: number, patch: Partial<FormFieldDef>) =>
    setFields((prev) => prev.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const addField = () => setFields((prev) => [...prev, { id: `f_${Date.now()}`, type: 'text', label: 'New field', name: `field_${prev.length + 1}`, required: false }]);
  const removeField = (i: number) => setFields((prev) => prev.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => setFields((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = fields.map((f) => ({ ...f, name: f.name?.trim() ? slug(f.name) : slug(f.label) }));
    onSubmit({ name, type, fields: cleaned, successMessage });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Form' : 'Create New Form'}</DialogTitle>
          <DialogDescription>Define the fields visitors fill in. Forms can be embedded on any page via the Form block.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter form name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Type</label>
              <select value={type} onChange={(e) => setType(e.target.value as FormItem['type'])} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="contact">Contact Form</option>
                <option value="lead">Lead Capture</option>
                <option value="survey">Survey</option>
                <option value="registration">Registration</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Fields</label>
              <button type="button" onClick={addField} className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"><Plus className="h-4 w-4" /> Add field</button>
            </div>
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={f.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Label" className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value as FormFieldDef['type'] })} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {f.type === 'select' && (
                    <input
                      value={(f.options || []).map((o) => o.label).join(', ')}
                      onChange={(e) => updateField(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map((s) => ({ label: s, value: s })) })}
                      placeholder="Options (comma-separated)"
                      className="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> Required
                    </label>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↑</button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↓</button>
                      <button type="button" onClick={() => removeField(i)} className="p-1 text-red-500 hover:text-red-700 text-xs">Remove</button>
                    </div>
                  </div>
                </div>
              ))}
              {fields.length === 0 && <p className="text-sm text-gray-400">No fields yet — add one above.</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Success message</label>
            <input type="text" value={successMessage} onChange={(e) => setSuccessMessage(e.target.value)} placeholder="Thanks — we’ll be in touch." className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>

          <DialogFooter>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{editing ? 'Save changes' : 'Create Form'}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface UploadAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (asset: Omit<AssetItem, 'id' | 'uploadedAt'>) => void;
}

function UploadAssetModal({ isOpen, onClose, onSubmit }: UploadAssetModalProps) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const getAssetType = (file: File): AssetItem['type'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'document';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = () => {
    if (selectedFile) {
      onSubmit({
        name: selectedFile.name,
        type: getAssetType(selectedFile),
        size: formatFileSize(selectedFile.size),
        url: `/assets/${selectedFile.name}`,
      });
      setSelectedFile(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Asset</DialogTitle>
          <DialogDescription>
            Upload images, videos, or documents to your media library.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Check className="h-6 w-6" />
                  <span className="font-medium">File selected</span>
                </div>
                <p className="text-sm text-gray-600">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">
                  Drag and drop your file here, or{' '}
                  <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                    browse
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*,video/*,.pdf,.doc,.docx"
                    />
                  </label>
                </p>
                <p className="text-xs text-gray-500">
                  Supports: Images, Videos, PDFs, Documents (Max 50MB)
                </p>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedFile}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: PageItem | null;
  onSubmit: (page: PageItem) => void;
}

function EditPageModal({ isOpen, onClose, page, onSubmit }: EditPageModalProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  React.useEffect(() => {
    if (page) {
      setTitle(page.title);
      setSlug(page.slug);
      setStatus(page.status);
    }
  }, [page]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (page) {
      onSubmit({
        ...page,
        title,
        slug,
        status,
        lastModified: new Date().toISOString().split('T')[0],
      });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Page</DialogTitle>
          <DialogDescription>
            Update the page details.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
  itemType: string;
}

function DeleteConfirmModal({ isOpen, onClose, onConfirm, itemName, itemType }: DeleteConfirmModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Delete {itemType}</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{itemName}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// PAGE EDITOR MODAL
// ============================================================================

interface PageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: PageItem | null;
  onSave: (page: PageItem) => void;
}

function PageEditorModal({ isOpen, onClose, page, onSave }: PageEditorModalProps) {
  const [activeSection, setActiveSection] = useState<'content' | 'design' | 'seo'>('content');
  const [content, setContent] = useState({
    heroTitle: 'Welcome to Our Platform',
    heroSubtitle: 'The best solution for your business needs',
    heroButtonText: 'Get Started',
    sections: [
      { type: 'text', content: 'Section 1 content here...' },
      { type: 'features', content: 'Features section' },
    ],
  });
  const [seo, setSeo] = useState({
    title: page?.title || '',
    description: '',
    keywords: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (page) {
      setSeo({ ...seo, title: page.title });
    }
  }, [page]);

  const handleSave = () => {
    if (!page) return;
    setIsSaving(true);
    setTimeout(() => {
      onSave({
        ...page,
        lastModified: new Date().toISOString().split('T')[0],
      });
      setIsSaving(false);
      onClose();
    }, 800);
  };

  if (!page) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layout className="h-5 w-5" />
            Page Editor: {page.title}
          </DialogTitle>
        </DialogHeader>

        {/* Editor Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 overflow-x-auto">
            {[
              { id: 'content', label: 'Content', icon: <Type className="h-4 w-4" /> },
              { id: 'design', label: 'Design', icon: <Layout className="h-4 w-4" /> },
              { id: 'seo', label: 'SEO', icon: <Search className="h-4 w-4" /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as typeof activeSection)}
                className={`flex items-center gap-2 py-2 px-3 border-b-2 text-sm font-medium ${
                  activeSection === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Editor Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {activeSection === 'content' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Hero Section</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.heroTitle}
                      onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={content.heroSubtitle}
                      onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input
                      type="text"
                      value={content.heroButtonText}
                      onChange={(e) => setContent({ ...content, heroButtonText: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Content Sections</h4>
                  <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <Plus className="h-4 w-4" />
                    Add Section
                  </button>
                </div>
                <div className="space-y-2">
                  {content.sections.map((section, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-white rounded border border-gray-200">
                      <Square className="h-4 w-4 text-gray-400" />
                      <span className="flex-1 text-sm">{section.type}: {section.content}</span>
                      <button className="p-1 hover:bg-gray-100 rounded">
                        <Edit className="h-4 w-4 text-gray-500" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 rounded">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'design' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Page Layout</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {['Full Width', 'Contained', 'Sidebar'].map((layout) => (
                    <button
                      key={layout}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 text-center"
                    >
                      <div className="h-12 bg-gray-200 rounded mb-2"></div>
                      <span className="text-sm text-gray-700">{layout}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Color Scheme</h4>
                <div className="flex gap-3">
                  {['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'].map((color) => (
                    <button
                      key={color}
                      className="w-10 h-10 rounded-full border-2 border-gray-200 hover:border-gray-400"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Typography</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heading Font</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                      <option>Inter</option>
                      <option>Roboto</option>
                      <option>Open Sans</option>
                      <option>Playfair Display</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Body Font</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                      <option>Inter</option>
                      <option>Roboto</option>
                      <option>Open Sans</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'seo' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Meta Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                    <input
                      type="text"
                      value={seo.title}
                      onChange={(e) => setSeo({ ...seo, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">{seo.title.length}/60 characters</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                    <textarea
                      rows={3}
                      value={seo.description}
                      onChange={(e) => setSeo({ ...seo, description: e.target.value })}
                      placeholder="Describe this page for search engines..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">{seo.description.length}/160 characters</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                    <input
                      type="text"
                      value={seo.keywords}
                      onChange={(e) => setSeo({ ...seo, keywords: e.target.value })}
                      placeholder="keyword1, keyword2, keyword3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Search Preview</h4>
                <div className="bg-white rounded p-3">
                  <p className="text-blue-600 text-lg">{seo.title || page.title}</p>
                  <p className="text-green-700 text-sm">example.com{page.slug}</p>
                  <p className="text-gray-600 text-sm">{seo.description || 'No description set'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Page
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// VIEW ASSET MODAL
// ============================================================================

interface ViewAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: AssetItem | null;
}

function ViewAssetModal({ isOpen, onClose, asset }: ViewAssetModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyUrl = () => {
    if (asset) {
      navigator.clipboard.writeText(window.location.origin + asset.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Asset Details</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {/* Preview */}
          <div className="bg-gray-100 rounded-lg p-8 flex items-center justify-center mb-4 min-h-[200px]">
            {asset.type === 'image' && (
              <div className="text-center">
                <Image className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Image Preview</p>
              </div>
            )}
            {asset.type === 'video' && (
              <div className="text-center">
                <Video className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Video Preview</p>
              </div>
            )}
            {asset.type === 'document' && (
              <div className="text-center">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Document Preview</p>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Filename</span>
              <span className="text-sm font-medium text-gray-900">{asset.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Type</span>
              <span className="text-sm font-medium text-gray-900 capitalize">{asset.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Size</span>
              <span className="text-sm font-medium text-gray-900">{asset.size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Uploaded</span>
              <span className="text-sm font-medium text-gray-900">{asset.uploadedAt}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">URL</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{asset.url}</span>
                <button
                  onClick={handleCopyUrl}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Copy URL"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <Download className="h-4 w-4" />
            Download
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// EDIT ASSET MODAL
// ============================================================================

interface EditAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: AssetItem | null;
  onSave: (asset: AssetItem) => void;
}

function EditAssetModal({ isOpen, onClose, asset, onSave }: EditAssetModalProps) {
  const [name, setName] = useState('');
  const [altText, setAltText] = useState('');

  React.useEffect(() => {
    if (asset) {
      setName(asset.name);
    }
  }, [asset]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (asset) {
      onSave({ ...asset, name });
      onClose();
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Asset</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filename</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {asset.type === 'image' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text (SEO)</label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe the image for accessibility"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

// AI brand studio — upload a logo and reference documents (PDFs, brand decks,
// notes), and/or give a seed color + notes, and AI proposes a brand identity
// (palette + fonts) which the user reviews and applies as design tokens.
// Uploads go to the S3-backed media library (real content management); the AI
// reads them (logo via vision, documents via text extraction). Self-contained.
function BrandTokenGenerator({ siteId, onApplied }: { siteId: string; onApplied: () => void }) {
  const [seed, setSeed] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logo, setLogo] = useState<{ name: string; url: string } | null>(null);
  const [docs, setDocs] = useState<Array<{ name: string; url: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef<HTMLInputElement | null>(null);
  const docsRef = useRef<HTMLInputElement | null>(null);

  const uploadLogo = async (file?: File) => {
    if (!file) return;
    setUploading(true); setError(null);
    const res = await webPresenceApi.uploadAsset(file);
    setUploading(false);
    if (res?.url) setLogo({ name: file.name, url: res.url });
    else setError('Logo upload failed — check you’re signed in, then try again.');
  };
  const uploadDocs = async (files?: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true); setError(null);
    const added: Array<{ name: string; url: string }> = [];
    for (const file of Array.from(files).slice(0, 8)) {
      const res = await webPresenceApi.uploadAsset(file);
      if (res?.url) added.push({ name: file.name, url: res.url });
    }
    setUploading(false);
    if (added.length) setDocs((prev) => [...prev, ...added].slice(0, 8));
    else setError('Document upload failed — check you’re signed in, then try again.');
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const hasUploads = !!logo || docs.length > 0;
      const res: any = hasUploads
        ? await webPresenceApi.aiAnalyzeDocuments({
            site_id: siteId || undefined,
            doc_urls: docs.map((d) => d.url),
            image_url: logo?.url,
            seed_color: seed.trim() || undefined,
            reference_text: notes.trim() || undefined,
          })
        : await webPresenceApi.aiGenerateTokens({
            site_id: siteId || undefined,
            seed_color: seed.trim() || undefined,
            reference_text: notes.trim() || undefined,
          });
      const tokens = (res && (res.data?.tokens || res.tokens)) || [];
      if (!tokens.length) setError('No tokens returned — add a logo, documents, a seed color, or notes and try again.');
      setResult(tokens);
    } catch (e: any) {
      setError(e?.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!result || !siteId) return;
    setApplying(true);
    setError(null);
    try {
      for (const t of result) {
        await webPresenceApi.createDesignToken({
          site_id: siteId,
          token_group: t.token_group,
          token_name: t.token_name,
          token_value: t.token_value,
          token_label: t.token_label || t.token_name,
        });
      }
      setResult(null);
      setSeed('');
      setNotes('');
      onApplied();
    } catch (e: any) {
      setError(e?.message || 'Failed to apply tokens');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <span className="text-sm font-medium text-gray-800">Generate a brand identity with AI</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Upload your logo and any reference documents (brand decks, PDFs, notes), and/or add a seed color
        and notes. AI reads them and proposes a brand palette + fonts. Review, then apply as design tokens.
      </p>

      {/* Upload sources — logo (vision) + reference documents (text-extracted) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> Logo</span>
            <button onClick={() => logoRef.current?.click()} disabled={uploading} className="text-xs text-violet-600 hover:text-violet-800 font-medium disabled:opacity-50">{uploading ? 'Uploading…' : logo ? 'Replace' : 'Upload'}</button>
          </div>
          {logo ? (
            <div className="flex items-center gap-2">
              <img src={logo.url} alt={logo.name} className="h-10 w-10 object-contain rounded border bg-white" />
              <span className="text-xs text-gray-500 truncate flex-1">{logo.name}</span>
              <button onClick={() => setLogo(null)} className="text-xs text-red-500">Remove</button>
            </div>
          ) : (
            <p className="text-xs text-gray-400">PNG/SVG/JPG — read with vision to extract brand colors.</p>
          )}
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Reference documents</span>
            <button onClick={() => docsRef.current?.click()} disabled={uploading} className="text-xs text-violet-600 hover:text-violet-800 font-medium disabled:opacity-50">{uploading ? 'Uploading…' : 'Add files'}</button>
          </div>
          {docs.length > 0 ? (
            <div className="space-y-1">
              {docs.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 truncate flex-1">{d.name}</span>
                  <button onClick={() => setDocs((prev) => prev.filter((_, j) => j !== i))} className="text-xs text-red-500">Remove</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">PDF, DOCX, TXT, MD — text extracted to inform the brand.</p>
          )}
          <input ref={docsRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.html,.csv,image/*" className="hidden" onChange={(e) => uploadDocs(e.target.files)} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
          placeholder="Seed color (e.g. #46b1e5)"
          className="px-3 py-1.5 border rounded-lg text-sm w-44"
        />
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Brand notes (optional)"
          className="px-3 py-1.5 border rounded-lg text-sm flex-1 min-w-[200px]"
        />
        <button
          onClick={generate}
          disabled={loading}
          className="px-4 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
        >
          {loading ? 'Generating…' : 'Generate'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {result && result.length > 0 && (
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {result.map((t, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1 border rounded-lg text-xs">
                {(t.token_group || '') === 'color' ? (
                  <span className="w-4 h-4 rounded-full border" style={{ background: t.token_value }} />
                ) : (
                  <span className="text-gray-400">Aa</span>
                )}
                <span className="font-medium">{t.token_label || t.token_name}</span>
                <span className="text-gray-500">{t.token_value}</span>
              </div>
            ))}
          </div>
          <button
            onClick={apply}
            disabled={applying || !siteId}
            className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {applying ? 'Applying…' : `Apply ${result.length} tokens`}
          </button>
        </div>
      )}
    </div>
  );
}

// Analyze a public reference website → suggested brand tokens. Fetches the site
// server-side, extracts palette/fonts/copy, and runs them through brand-token
// generation; the user reviews detected signals + tokens, then applies.
function ReferenceSiteAnalyzer({ siteId, onApplied }: { siteId: string; onApplied: () => void }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<any[] | null>(null);
  const [detected, setDetected] = useState<{ colors?: string[]; fonts?: string[]; title?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = async () => {
    if (!url.trim()) { setError('Enter a website URL first.'); return; }
    setLoading(true); setError(null); setResult(null); setDetected(null);
    try {
      const res: any = await webPresenceApi.aiAnalyzeSite({ url: url.trim(), site_id: siteId || undefined });
      const payload = res?.data || res || {};
      const tokens = payload.tokens || [];
      setDetected(payload.detected || null);
      if (!tokens.length) setError('Couldn’t derive a brand from that site — try a different URL.');
      setResult(tokens);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Analysis failed — check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!result || !siteId) return;
    setApplying(true); setError(null);
    try {
      for (const t of result) {
        await webPresenceApi.createDesignToken({
          site_id: siteId,
          token_group: t.token_group,
          token_name: t.token_name,
          token_value: t.token_value,
          token_label: t.token_label || t.token_name,
        });
      }
      setResult(null); setDetected(null); setUrl('');
      onApplied();
    } catch (e: any) {
      setError(e?.message || 'Failed to apply tokens');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg border">
      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><Globe className="h-4 w-4" /> Reference External Site</h4>
      <p className="text-xs text-gray-500 mb-3">Enter a URL to analyze its design — AI extracts colors, fonts, and positioning and proposes brand tokens you can review and apply.</p>
      <div className="flex gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') analyze(); }}
          placeholder="https://your-existing-site.com"
          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
        <button onClick={analyze} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 flex items-center gap-2 disabled:opacity-50">
          <Search className="h-4 w-4" /> {loading ? 'Analyzing…' : 'Analyze'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {detected && (detected.colors?.length || detected.fonts?.length) ? (
        <div className="mt-3 text-xs text-gray-600">
          {detected.colors && detected.colors.length > 0 && (
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-gray-400">Detected colors:</span>
              {detected.colors.map((c, i) => (
                <span key={i} className="flex items-center gap-1"><span className="w-4 h-4 rounded-full border" style={{ background: c }} />{c}</span>
              ))}
            </div>
          )}
          {detected.fonts && detected.fonts.length > 0 && (
            <div><span className="text-gray-400">Detected fonts:</span> {detected.fonts.join(', ')}</div>
          )}
        </div>
      ) : null}
      {result && result.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2 mb-3">
            {result.map((t, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1 border rounded-lg text-xs bg-white">
                {(t.token_group || '') === 'color' ? (
                  <span className="w-4 h-4 rounded-full border" style={{ background: t.token_value }} />
                ) : (
                  <span className="text-gray-400">Aa</span>
                )}
                <span className="font-medium">{t.token_label || t.token_name}</span>
                <span className="text-gray-500">{t.token_value}</span>
              </div>
            ))}
          </div>
          <button onClick={apply} disabled={applying || !siteId} className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
            {applying ? 'Applying…' : `Apply ${result.length} tokens`}
          </button>
        </div>
      )}
    </div>
  );
}

interface PagesTabProps {
  pages: PageItem[];
  onCreatePage: () => void;
  onEditPage: (page: PageItem) => void;
  onDesignPage: (page: PageItem) => void;
  onDeletePage: (page: PageItem) => void;
  /** Designate a page as the public site's home (deterministic mapping). */
  onSetHome?: (page: PageItem) => void;
  /** Currently-designated home page id (from site brand_settings.home_page_id). */
  homePageId?: string;
  /** Full-screen public preview (renders what would deploy, without publishing). */
  onPreview?: (page: PageItem) => void;
  /** Explicit go-live: publish + set as public home. */
  onDeploy?: (page: PageItem) => void;
  /** Take a live page back down (→ built-in hero). */
  onRevert?: (page: PageItem) => void;
}

function PagesTab({ pages, onCreatePage, onEditPage, onDesignPage, onDeletePage, onSetHome, homePageId, onPreview, onDeploy, onRevert }: PagesTabProps) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Website Pages</h3>
        <button
          onClick={onCreatePage}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          New Page
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pages.map((page) => (
              <tr key={page.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900">{page.title}</div>
                    <div className="text-sm text-gray-500">{page.slug}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    const isLive = page.status === 'published' && homePageId === page.id;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isLive ? 'bg-green-100 text-green-800'
                          : page.status === 'published' ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {isLive && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                        {isLive ? 'Live' : page.status}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{page.views.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{page.lastModified}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Preview (how it looks live — does not deploy)"
                      onClick={() => (onPreview || onDesignPage)(page)}
                    >
                      <Eye className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Design (layered builder)"
                      onClick={() => onDesignPage(page)}
                    >
                      <Layout className="h-4 w-4 text-violet-600" />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Edit Settings"
                      onClick={() => onEditPage(page)}
                    >
                      <Settings className="h-4 w-4 text-gray-500" />
                    </button>
                    {/* Explicit deploy / revert — the deliberate go-live control. */}
                    {onDeploy && !(page.status === 'published' && homePageId === page.id) && (
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white bg-violet-600 hover:bg-violet-700"
                        title="Deploy to public site"
                        onClick={() => onDeploy(page)}
                      >
                        <Rocket className="h-3.5 w-3.5" /> Deploy
                      </button>
                    )}
                    {onRevert && page.status === 'published' && homePageId === page.id && (
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
                        title="Take down from public site (revert to built-in hero)"
                        onClick={() => onRevert(page)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Take down
                      </button>
                    )}
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Delete"
                      onClick={() => onDeletePage(page)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface LandingPagesTabProps {
  pages: PageItem[];
  onCreatePage: () => void;
  onEditPage: (page: PageItem) => void;
  onDesignPage: (page: PageItem) => void;
}

function LandingPagesTab({ pages, onCreatePage, onEditPage, onDesignPage }: LandingPagesTabProps) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Landing Pages</h3>
        <button
          onClick={onCreatePage}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create Landing Page
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pages.map((page) => (
          <div key={page.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="h-32 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <FileText className="h-12 w-12 text-white opacity-50" />
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{page.title}</h4>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  page.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {page.status}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-3">{page.slug}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{page.views.toLocaleString()} views</span>
                <div className="flex items-center gap-2">
                  <button className="p-1 hover:bg-gray-100 rounded" title="Preview" onClick={() => onDesignPage(page)}>
                    <Eye className="h-4 w-4 text-gray-500" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Design Page"
                    onClick={() => onDesignPage(page)}
                  >
                    <Layout className="h-4 w-4 text-blue-500" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Edit Settings"
                    onClick={() => onEditPage(page)}
                  >
                    <Settings className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface FormsTabProps {
  forms: FormItem[];
  onCreateForm: () => void;
  onEditForm: (form: FormItem) => void;
}

function FormsTab({ forms, onCreateForm, onEditForm }: FormsTabProps) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Forms</h3>
        <button
          onClick={onCreateForm}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Create Form
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {forms.map((form) => (
          <div key={form.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-medium text-gray-900">{form.name}</h4>
                <span className="text-sm text-gray-500 capitalize">{form.type} · {(form.fields || []).length} field{(form.fields || []).length === 1 ? '' : 's'}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onEditForm(form)} title="Edit form" className="p-1 hover:bg-gray-100 rounded">
                  <Settings className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900">{form.submissions}</p>
                <p className="text-sm text-gray-500">Submissions</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{form.conversionRate}%</p>
                <p className="text-sm text-gray-500">Conversion</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{form.lastSubmission}</p>
                <p className="text-sm text-gray-500">Last submission</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AssetsTabProps {
  assets: AssetItem[];
  onUploadAsset: () => void;
  onViewAsset: (asset: AssetItem) => void;
  onEditAsset: (asset: AssetItem) => void;
  onDeleteAsset: (asset: AssetItem) => void;
}

function AssetsTab({ assets, onUploadAsset, onViewAsset, onEditAsset, onDeleteAsset }: AssetsTabProps) {
  const [filterType, setFilterType] = useState<string>('all');

  const filteredAssets = filterType === 'all' ? assets : assets.filter(a => a.type === filterType);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Media Library</h3>
        <div className="flex items-center gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
          </select>
          <button
            onClick={onUploadAsset}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Upload Asset
          </button>
        </div>
      </div>

      {filteredAssets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Image className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No assets found</p>
          <button
            onClick={onUploadAsset}
            className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
          >
            Upload your first asset
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredAssets.map((asset) => (
            <div key={asset.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden group relative">
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {asset.type === 'image' && <Image className="h-8 w-8 text-gray-400" />}
                {asset.type === 'video' && <Video className="h-8 w-8 text-gray-400" />}
                {asset.type === 'document' && <FileText className="h-8 w-8 text-gray-400" />}
              </div>
              <div className="p-2">
                <p className="text-sm font-medium text-gray-900 truncate">{asset.name}</p>
                <p className="text-xs text-gray-500">{asset.size}</p>
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  onClick={() => onViewAsset(asset)}
                  title="View"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  onClick={() => onEditAsset(asset)}
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                  onClick={() => onDeleteAsset(asset)}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SEOTab() {
  const [activeSection, setActiveSection] = useState<'overview' | 'meta' | 'audit'>('overview');
  const [seoSettings, setSeoSettings] = useState({
    defaultTitle: 'Trust Platform - Marketing & Sales Automation',
    defaultDescription: 'Streamline your marketing and sales operations with our all-in-one platform.',
    ogImage: '',
    twitterCard: 'summary_large_image',
    canonicalUrl: '',
    robotsTxt: 'User-agent: *\nAllow: /',
    sitemap: 'https://example.com/sitemap.xml',
  });
  const [metaTags, setMetaTags] = useState([
    { id: '1', name: 'author', content: 'Trust Platform' },
    { id: '2', name: 'robots', content: 'index, follow' },
    { id: '3', property: 'og:type', content: 'website' },
  ]);
  const [newMetaTag, setNewMetaTag] = useState({ name: '', content: '' });
  const [saved, setSaved] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState<null | {
    score: number;
    issues: { type: 'error' | 'warning' | 'success'; message: string }[];
  }>(null);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleRunAudit = () => {
    setIsAuditing(true);
    setTimeout(() => {
      setAuditResults({
        score: 85,
        issues: [
          { type: 'success', message: 'All pages have meta descriptions' },
          { type: 'success', message: 'Title tags are properly formatted' },
          { type: 'success', message: 'Canonical URLs are set correctly' },
          { type: 'warning', message: '3 images missing alt text' },
          { type: 'warning', message: 'Some pages have slow load times (>3s)' },
          { type: 'error', message: '1 page has duplicate meta description' },
          { type: 'success', message: 'Sitemap is accessible and valid' },
          { type: 'success', message: 'Robots.txt is properly configured' },
        ],
      });
      setIsAuditing(false);
    }, 2000);
  };

  const handleAddMetaTag = () => {
    if (newMetaTag.name && newMetaTag.content) {
      setMetaTags([...metaTags, { id: String(Date.now()), ...newMetaTag }]);
      setNewMetaTag({ name: '', content: '' });
    }
  };

  const handleRemoveMetaTag = (id: string) => {
    setMetaTags(metaTags.filter(t => t.id !== id));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">SEO Settings</h3>
        <button
          onClick={handleRunAudit}
          disabled={isAuditing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isAuditing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Running Audit...
            </>
          ) : (
            <>
              <Search className="h-4 w-4" />
              Run SEO Audit
            </>
          )}
        </button>
      </div>

      {/* Section Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-3 sm:space-x-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'meta', label: 'Meta Tags' },
            { id: 'audit', label: 'Audit Results' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id as typeof activeSection)}
              className={`py-2 border-b-2 text-sm font-medium ${
                activeSection === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSection === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">SEO Score</h4>
            <div className="flex items-center gap-4">
              <div className={`w-24 h-24 rounded-full border-8 flex items-center justify-center ${
                (auditResults?.score || 85) >= 80 ? 'border-green-500' :
                (auditResults?.score || 85) >= 60 ? 'border-yellow-500' : 'border-red-500'
              }`}>
                <span className="text-2xl font-bold text-gray-900">{auditResults?.score || 85}</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Your site's SEO health is good.</p>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Meta descriptions: Optimized
                  </li>
                  <li className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Title tags: Optimized
                  </li>
                  <li className="flex items-center gap-2 text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    Image alt text: Needs improvement
                  </li>
                  <li className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    Page speed: Below average
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Top Keywords</h4>
            <div className="space-y-3">
              {['marketing automation', 'sales pipeline', 'CRM software', 'lead generation'].map((keyword, i) => (
                <div key={keyword} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{keyword}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">#{i + 1}</span>
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
            <h4 className="font-medium text-gray-900 mb-4">Default SEO Settings</h4>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Title Tag</label>
                  <input
                    type="text"
                    value={seoSettings.defaultTitle}
                    onChange={(e) => setSeoSettings({ ...seoSettings, defaultTitle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">{seoSettings.defaultTitle.length}/60 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sitemap URL</label>
                  <input
                    type="text"
                    value={seoSettings.sitemap}
                    onChange={(e) => setSeoSettings({ ...seoSettings, sitemap: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Meta Description</label>
                <textarea
                  rows={2}
                  value={seoSettings.defaultDescription}
                  onChange={(e) => setSeoSettings({ ...seoSettings, defaultDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{seoSettings.defaultDescription.length}/160 characters</p>
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                {saved ? (
                  <>
                    <Check className="h-4 w-4" />
                    Saved!
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeSection === 'meta' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Add Custom Meta Tag</h4>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Name (e.g., author)"
                value={newMetaTag.name}
                onChange={(e) => setNewMetaTag({ ...newMetaTag, name: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Content"
                value={newMetaTag.content}
                onChange={(e) => setNewMetaTag({ ...newMetaTag, content: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddMetaTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Tag
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Current Meta Tags</h4>
            <div className="space-y-2">
              {metaTags.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-mono text-sm text-blue-600">{tag.name || tag.property}</span>
                    <span className="text-gray-400 mx-2">=</span>
                    <span className="text-sm text-gray-700">"{tag.content}"</span>
                  </div>
                  <button
                    onClick={() => handleRemoveMetaTag(tag.id)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Open Graph Settings</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label>
                <input
                  type="text"
                  value={seoSettings.ogImage}
                  onChange={(e) => setSeoSettings({ ...seoSettings, ogImage: e.target.value })}
                  placeholder="https://example.com/og-image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Twitter Card Type</label>
                <select
                  value={seoSettings.twitterCard}
                  onChange={(e) => setSeoSettings({ ...seoSettings, twitterCard: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">Summary with Large Image</option>
                  <option value="player">Player</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'audit' && (
        <div className="space-y-6">
          {auditResults ? (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900">Audit Summary</h4>
                  <span className={`text-2xl font-bold ${
                    auditResults.score >= 80 ? 'text-green-600' :
                    auditResults.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {auditResults.score}/100
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {auditResults.issues.filter(i => i.type === 'success').length}
                    </p>
                    <p className="text-sm text-green-700">Passed</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">
                      {auditResults.issues.filter(i => i.type === 'warning').length}
                    </p>
                    <p className="text-sm text-yellow-700">Warnings</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {auditResults.issues.filter(i => i.type === 'error').length}
                    </p>
                    <p className="text-sm text-red-700">Errors</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-medium text-gray-900 mb-4">Detailed Results</h4>
                <div className="space-y-2">
                  {auditResults.issues.map((issue, idx) => (
                    <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${
                      issue.type === 'success' ? 'bg-green-50' :
                      issue.type === 'warning' ? 'bg-yellow-50' : 'bg-red-50'
                    }`}>
                      {issue.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
                      {issue.type === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
                      {issue.type === 'error' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                      <span className={`text-sm ${
                        issue.type === 'success' ? 'text-green-800' :
                        issue.type === 'warning' ? 'text-yellow-800' : 'text-red-800'
                      }`}>
                        {issue.message}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-3">No audit results yet</p>
              <button
                onClick={handleRunAudit}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Run your first SEO audit
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function WebPresencePage({ mode, initialTab }: { mode?: 'web' | 'comms'; initialTab?: WebPresenceTab } = {}) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const isTriveny = productConfig.isTriveny;
  const isCommsMode = mode === 'comms';
  const [activeTab, setActiveTab] = useState<WebPresenceTab>(initialTab || 'ai');
  // Agent-first: in web mode the assistant is the centerpiece; the tabs are demoted
  // behind an "Advanced tools" toggle.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Inbound prompt from TrivenyHome's Design pillar — consumed once on mount,
  // routes the user to the matching sub-tab and pre-fills the AI composer.
  const [inboundPrompt, setInboundPrompt] = useState<string | null>(null);
  useEffect(() => {
    if (!isTriveny) return;
    let cancelled = false;
    (async () => {
      const mod = await import('../../shared/stores/promptHandoff');
      const pending = mod.consumePendingPrompt(mod.PROMPT_SURFACES.studio);
      if (!pending || cancelled) return;
      // intent='landing' → AI Studio page generator; 'email' → emails tab;
      // 'forms' → Manage › Forms tab.
      if (pending.intent === 'email') setActiveTab('emails');
      else if (pending.intent === 'forms') setActiveTab('forms');
      else setActiveTab('ai');
      setInboundPrompt(pending.prompt);
    })();
    return () => { cancelled = true; };
  }, [isTriveny]);

  // State for data
  const [pages, setPages] = useState<PageItem[]>([]);
  const [landingPages, setLandingPages] = useState<PageItem[]>([]);
  const [forms, setForms] = useState<FormItem[]>([]);
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [createPageModalOpen, setCreatePageModalOpen] = useState(false);
  const [createLandingPageModalOpen, setCreateLandingPageModalOpen] = useState(false);
  const [createFormModalOpen, setCreateFormModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<FormItem | null>(null);
  const [uploadAssetModalOpen, setUploadAssetModalOpen] = useState(false);
  const [editPageModalOpen, setEditPageModalOpen] = useState(false);
  const [pageEditorOpen, setPageEditorOpen] = useState(false);
  const [viewAssetModalOpen, setViewAssetModalOpen] = useState(false);
  const [editAssetModalOpen, setEditAssetModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // AI Studio preview
  const [aiPreviewBlocks, setAiPreviewBlocks] = useState<any[]>([]);

  // Tab group expand/collapse — auto-expand the group containing the active tab
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['create']));

  // Pages filter (merged web + landing)
  const [pageTypeFilter, setPageTypeFilter] = useState<'all' | 'page' | 'landing'>('all');

  // Content outputs (posts, emails, flyers)
  const [contentOutputs, setContentOutputs] = useState<any[]>([]);

  // Selected items for edit/delete
  const [selectedPage, setSelectedPage] = useState<PageItem | null>(null);
  const [selectedPageRaw, setSelectedPageRaw] = useState<any>(null);
  // Full-screen public preview overlay (renders exactly what would deploy,
  // WITHOUT publishing). Holds a PublicSitePage-shaped object.
  const [previewPage, setPreviewPage] = useState<any>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ name: string; type: string; onConfirm: () => void } | null>(null);

  // New tab data states
  const [designTokens, setDesignTokens] = useState<DesignToken[]>([]);
  const [codeSnippets, setCodeSnippets] = useState<CodeSnippet[]>([]);
  const [permissions, setPermissions] = useState<WebPresencePermission[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [roleMappings, setRoleMappings] = useState<any[]>([]);
  const [activityEntries, setActivityEntries] = useState<ActivityLogEntry[]>([]);
  const [redirects, setRedirects] = useState<any[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [sites, setSites] = useState<any[]>([]);
  const [designSubTab, setDesignSubTab] = useState<'brand' | 'tokens' | 'templates' | 'import'>('brand');
  const [seoSubTab, setSeoSubTab] = useState<'overview' | 'bulk' | 'redirects'>('overview');

  // Load sites + pages/forms/assets on mount
  // Lightweight page-list refresh (no loading flash) — called when the Design Studio
  // dock builds a page by voice or text, so the Pages tab reflects it immediately.
  const reloadPages = useCallback(async () => {
    if (!activeSiteId) return;
    try {
      const pageRes = await getPlatformApi().get<any[]>(`/web-presence/sites/${activeSiteId}/pages`);
      if (pageRes.success && pageRes.data) {
        setPages((pageRes.data as any[]).filter((p: any) => p.page_type === 'page' || !p.page_type).map(mapApiPage));
        setLandingPages((pageRes.data as any[]).filter((p: any) => p.page_type === 'landing_page' || p.page_type === 'landing').map(mapApiPage));
      }
    } catch { /* ignore */ }
  }, [activeSiteId]);

  const loadSitesAndData = useCallback(async () => {
    try {
      let siteRes = await webPresenceApi.getSites();
      let siteId = '';

      // Auto-create a default site if none exists
      if (!siteRes.success || !siteRes.data || (siteRes.data as any[]).length === 0) {
        const createRes = await webPresenceApi.createSite({ site_name: 'Main Website', site_type: 'primary' });
        if (createRes.success && createRes.data) {
          siteId = createRes.data.id;
          setSites([createRes.data]);
          setActiveSiteId(siteId);
        }
      } else {
        setSites(siteRes.data);
        siteId = (siteRes.data as any[])[0].id;
        setActiveSiteId(siteId);
      }

      if (!siteId) {
        setIsLoading(false);
        return;
      }

      // Load pages for this site
      try {
        const pageRes = await getPlatformApi().get<any[]>(`/web-presence/sites/${siteId}/pages`);
        if (pageRes.success && pageRes.data) {
          setPages((pageRes.data as any[]).filter((p: any) => p.page_type === 'page' || !p.page_type).map(mapApiPage));
          setLandingPages((pageRes.data as any[]).filter((p: any) => p.page_type === 'landing_page' || p.page_type === 'landing').map(mapApiPage));
        }
      } catch { /* no pages yet */ }

      // Load landing pages from dedicated endpoint
      try {
        const lpRes = await getPlatformApi().get<any[]>('/web-presence/landing-pages');
        if (lpRes.success && lpRes.data && (lpRes.data as any[]).length > 0) {
          setLandingPages((lpRes.data as any[]).map(mapApiPage));
        }
      } catch { /* no landing pages yet */ }

      // Load forms
      try {
        const formRes = await webPresenceApi.getForms();
        if (formRes.success && formRes.data) {
          setForms((formRes.data as any[]).map(mapApiForm));
        }
      } catch { /* no forms yet */ }

      // Load assets
      try {
        const assetRes = await webPresenceApi.getAssets();
        if (assetRes.success && assetRes.data) {
          setAssets((assetRes.data as any[]).map((a: any) => ({
            id: a.id,
            name: a.file_name || a.name || '',
            type: a.file_type || a.type || 'document',
            size: a.file_size || a.size || '',
            url: a.file_url || a.url || '',
            uploadedAt: a.created_at ? new Date(a.created_at).toISOString().split('T')[0] : '',
          })));
        }
      } catch { /* no assets yet */ }
    } catch (err) {
      console.error('Failed to load web presence data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSitesAndData();
  }, [loadSitesAndData]);

  React.useEffect(() => {
    if (activeTab === 'design' && activeSiteId) {
      webPresenceApi.getDesignTokens(activeSiteId).then(res => {
        if (res.success && res.data) setDesignTokens(res.data);
      });
    }
    if (activeTab === 'code') {
      webPresenceApi.getCodeSnippets().then(res => {
        if (res.success && res.data) setCodeSnippets(res.data);
      });
    }
    if (activeTab === 'team') {
      webPresenceApi.getPermissions().then(res => {
        if (res.success && res.data) setPermissions(res.data);
      });
      webPresenceApi.getTeamMembers().then(res => {
        if (res.success && res.data) setTeamMembers(res.data);
      }).catch(() => { /* team members endpoint may not be available yet */ });
      webPresenceApi.getRoleMappings().then(res => {
        if (res.success && res.data) setRoleMappings(res.data);
      }).catch(() => { /* role mappings endpoint may not be available yet */ });
    }
    if (activeTab === 'activity') {
      webPresenceApi.getActivityLog().then(res => {
        if (res.success && res.data) setActivityEntries(res.data);
      });
    }
    if (activeTab === 'seo' && activeSiteId) {
      webPresenceApi.getRedirects(activeSiteId).then(res => {
        if (res.success && res.data) setRedirects(res.data);
      });
    }
  }, [activeTab, activeSiteId]);

  const tabGroups: { id: string; label: string; tabs: { id: WebPresenceTab; label: string; icon: React.ReactNode }[] }[] = isCommsMode ? [
    {
      id: 'create', label: 'Create',
      tabs: [
        { id: 'ai', label: 'AI Studio', icon: <Sparkles className="h-4 w-4" /> },
        { id: 'posts', label: 'Posts & Social', icon: <Share2 className="h-4 w-4" /> },
        { id: 'emails', label: 'Emails', icon: <Mail className="h-4 w-4" /> },
        { id: 'sms', label: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
        { id: 'voice_ai', label: 'Voice AI', icon: <Mic className="h-4 w-4" /> },
        { id: 'flyers', label: 'Flyers & Print', icon: <Printer className="h-4 w-4" /> },
      ],
    },
    {
      id: 'manage', label: 'Manage',
      tabs: [
        { id: 'assets', label: 'Assets & Media', icon: <Image className="h-4 w-4" /> },
        { id: 'forms', label: 'Forms', icon: <FormInput className="h-4 w-4" /> },
        { id: 'content', label: 'Content', icon: <Database className="h-4 w-4" /> },
      ],
    },
    {
      id: 'settings', label: 'Settings',
      tabs: [
        { id: 'design', label: 'Brand & Design', icon: <Palette className="h-4 w-4" /> },
        { id: 'activity', label: 'Activity', icon: <Activity className="h-4 w-4" /> },
      ],
    },
  ] : [
    {
      id: 'create', label: 'Create',
      tabs: [
        { id: 'ai', label: 'AI Studio', icon: <Sparkles className="h-4 w-4" /> },
        { id: 'pages', label: 'Pages', icon: <FileText className="h-4 w-4" /> },
        { id: 'posts', label: 'Posts & Social', icon: <Share2 className="h-4 w-4" /> },
        { id: 'emails', label: 'Emails', icon: <Mail className="h-4 w-4" /> },
        { id: 'flyers', label: 'Flyers & Print', icon: <Printer className="h-4 w-4" /> },
      ],
    },
    {
      id: 'manage', label: 'Manage',
      tabs: [
        { id: 'assets', label: 'Assets & Media', icon: <Image className="h-4 w-4" /> },
        { id: 'forms', label: 'Forms', icon: <FormInput className="h-4 w-4" /> },
        { id: 'content', label: 'Content', icon: <Database className="h-4 w-4" /> },
        { id: 'seo', label: 'SEO', icon: <Search className="h-4 w-4" /> },
      ],
    },
    {
      id: 'settings', label: 'Settings',
      tabs: [
        { id: 'design', label: 'Brand & Design', icon: <Palette className="h-4 w-4" /> },
        { id: 'code', label: 'Code', icon: <Code2 className="h-4 w-4" /> },
        { id: 'team', label: 'Team', icon: <Shield className="h-4 w-4" /> },
        { id: 'activity', label: 'Activity', icon: <Activity className="h-4 w-4" /> },
      ],
    },
  ];

  // Handlers
  const handleCreatePage = async (page: Omit<PageItem, 'id' | 'views' | 'lastModified'>) => {
    try {
      const res = await webPresenceApi.createPage({
        title: page.title,
        slug: page.slug,
        page_type: 'page',
        site_id: activeSiteId,
        status: page.status,
      } as any);
      if (res.success && res.data) {
        const newPage = mapApiPage(res.data);
        setPages(prev => [...prev, newPage]);
        // Open the unified builder for the new page.
        window.location.href = `/design/v2?page=${encodeURIComponent(newPage.id)}`;
      }
    } catch (err) {
      console.error('Failed to create page:', err);
    }
  };

  const handleCreateLandingPage = async (page: Omit<PageItem, 'id' | 'views' | 'lastModified'>) => {
    try {
      const res = await webPresenceApi.createLandingPage({
        headline: page.title,
        campaign_name: page.slug,
        site_id: activeSiteId,
      } as any);
      if (res.success && res.data) {
        const newPage = mapApiPage(res.data);
        setLandingPages(prev => [...prev, newPage]);
        // Open the unified builder for the new landing page.
        window.location.href = `/design/v2?page=${encodeURIComponent(newPage.id)}`;
      }
    } catch (err) {
      console.error('Failed to create landing page:', err);
    }
  };

  const handleEditPage = async (updatedPage: PageItem) => {
    try {
      await webPresenceApi.updatePage(updatedPage.id, {
        title: updatedPage.title,
        slug: updatedPage.slug,
        status: updatedPage.status,
      } as any);
      if (activeTab === 'pages') {
        setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
      } else {
        setLandingPages(landingPages.map(p => p.id === updatedPage.id ? updatedPage : p));
      }
    } catch (err) {
      console.error('Failed to update page:', err);
    }
  };

  const handleDeletePage = (page: PageItem) => {
    setItemToDelete({
      name: page.title,
      type: 'Page',
      onConfirm: async () => {
        try {
          await webPresenceApi.deletePage(page.id);
          setPages(pages.filter(p => p.id !== page.id));
        } catch (err) {
          console.error('Failed to delete page:', err);
        }
      },
    });
    setDeleteConfirmOpen(true);
  };

  // --- Public-site deploy workflow ------------------------------------------
  // Studio pages are DRAFTS by default and never auto-deploy (the public API
  // only serves published pages). Preview renders exactly what would ship
  // WITHOUT publishing; Deploy is the explicit, deliberate go-live; Revert
  // takes it back down so the public site falls back to the built-in hero.
  const resolveSiteBrand = () => {
    const site = sites.find((x) => x.id === activeSiteId);
    const bs =
      (site && (typeof site.brand_settings === 'string'
        ? JSON.parse(site.brand_settings || '{}')
        : site.brand_settings)) || {};
    return { site, bs };
  };

  const handlePreviewPage = async (page: PageItem) => {
    let raw: any = null;
    try {
      const res = await getPlatformApi().get(`/web-presence/pages/${page.id}`);
      raw = res.success && res.data ? res.data : null;
    } catch {
      /* fall back to empty preview */
    }
    const { bs } = resolveSiteBrand();
    setPreviewPage({
      title: page.title,
      content_blocks: (raw && raw.content_blocks) || [],
      brand_settings: bs,
      design_tokens: [],
    });
  };

  const handleDeployPage = async (page: PageItem) => {
    if (!window.confirm(
      `Deploy “${page.title}” to your public site?\n\nIt will go LIVE immediately at your public domain and replace whatever visitors see today.`
    )) return;
    try {
      await webPresenceApi.updatePage(page.id, { status: 'published' } as any);
      // Designate it as the public home (deterministic host→home mapping).
      const { site, bs } = resolveSiteBrand();
      const nextBrand = { ...bs, home_page_id: page.id };
      const res: any = await getPlatformApi().patch(`/web-presence/sites/${activeSiteId}`, { brand_settings: nextBrand });
      const updated = res && res.data ? res.data : { ...(site || {}), brand_settings: nextBrand };
      setSites((prev) => prev.map((x) => (x.id === activeSiteId ? updated : x)));
      setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, status: 'published' } : p)));
      setLandingPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, status: 'published' } : p)));
    } catch (e) {
      console.error('Deploy failed:', e);
      window.alert('Deploy failed — please try again.');
    }
  };

  const handleRevertPage = async (page: PageItem) => {
    if (!window.confirm(
      `Take “${page.title}” off your public site?\n\nVisitors will see the built-in hero again until you deploy a page.`
    )) return;
    try {
      await webPresenceApi.updatePage(page.id, { status: 'draft' } as any);
      const { site, bs } = resolveSiteBrand();
      if (bs.home_page_id === page.id) {
        const nextBrand = { ...bs, home_page_id: null };
        const res: any = await getPlatformApi().patch(`/web-presence/sites/${activeSiteId}`, { brand_settings: nextBrand });
        const updated = res && res.data ? res.data : { ...(site || {}), brand_settings: nextBrand };
        setSites((prev) => prev.map((x) => (x.id === activeSiteId ? updated : x)));
      }
      setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, status: 'draft' } : p)));
      setLandingPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, status: 'draft' } : p)));
    } catch (e) {
      console.error('Revert failed:', e);
      window.alert('Revert failed — please try again.');
    }
  };

  const handleCreateForm = async (data: { name: string; type: FormItem['type']; fields: FormFieldDef[]; successMessage: string }) => {
    try {
      const res = await webPresenceApi.createForm({
        form_name: data.name,
        form_type: data.type,
        fields: data.fields,
        success_message: data.successMessage || undefined,
      } as any);
      if (res.success && res.data) {
        setForms(prev => [...prev, mapApiForm(res.data)]);
      }
    } catch (err) {
      console.error('Failed to create form:', err);
    }
  };

  const handleUpdateForm = async (id: string, data: { name: string; type: FormItem['type']; fields: FormFieldDef[]; successMessage: string }) => {
    try {
      const res = await webPresenceApi.updateForm(id, {
        form_name: data.name,
        form_type: data.type,
        fields: data.fields,
        success_message: data.successMessage || undefined,
      });
      if (res.success && res.data) {
        setForms(prev => prev.map((f) => (f.id === id ? mapApiForm(res.data) : f)));
      }
    } catch (err) {
      console.error('Failed to update form:', err);
    }
  };

  const handleUploadAsset = (asset: Omit<AssetItem, 'id' | 'uploadedAt'>) => {
    const newAsset: AssetItem = {
      ...asset,
      id: String(assets.length + 1),
      uploadedAt: new Date().toISOString().split('T')[0],
    };
    setAssets([...assets, newAsset]);
  };

  const handleDeleteAsset = (asset: AssetItem) => {
    setItemToDelete({
      name: asset.name,
      type: 'Asset',
      onConfirm: async () => {
        try {
          await webPresenceApi.deleteAsset(asset.id);
          setAssets(assets.filter(a => a.id !== asset.id));
        } catch (err) {
          console.error('Failed to delete asset:', err);
        }
      },
    });
    setDeleteConfirmOpen(true);
  };

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'p-6 max-w-7xl mx-auto'}
      style={
        isTriveny
          ? { minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans, color: tvTokens.ink }
          : undefined
      }
    >
      {/* Header — the top pillar bar (TvPillarBar: favicon + "Design ▸ Studio") is the
          single banner; the page-level TvPageBar was a duplicate and is removed. */}
      {!isTriveny && !isCommsMode && (
        <div className="mb-8">
          {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Sparkles className="h-6 w-6 text-violet-600" />{t('platform.designStudio.title', 'Design Studio')}</h1>}
          <p className="text-gray-600 mt-1">{t('platform.designStudio.description', 'AI-powered content creation, page design, assets, and brand management')}</p>
        </div>
      )}
      {isCommsMode && (
        <div className="mb-4" />
      )}
      <div style={isTriveny ? { padding: '20px 22px 24px' } : undefined}>

      {/* Metrics bar removed — the Design Studio is agent-first (the assistant is the centerpiece). */}

      {/* Advanced-tools toggle — the tabs are demoted so the assistant leads (web mode). */}
      {!isCommsMode && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: showAdvanced ? 4 : 0 }}>
          <button
            onClick={() => { const next = !showAdvanced; setShowAdvanced(next); if (!next) setActiveTab('ai'); }}
            style={{ fontSize: 12, fontWeight: 500, color: tvTokens.ink500, background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
          >
            {showAdvanced ? '‹ Back to assistant' : 'Advanced tools ›'}
          </button>
        </div>
      )}

      {/* Collapsible Grouped Tabs (hidden by default in web mode) */}
      <div className="border-b border-gray-200 mb-6" style={{ display: (isCommsMode || showAdvanced) ? undefined : 'none' }}>
        <nav className="flex items-center overflow-x-auto">
          {tabGroups.map((group, gi) => {
            const isExpanded = expandedGroups.has(group.id);
            const groupHasActiveTab = group.tabs.some(t => t.id === activeTab);
            const activeTabInGroup = group.tabs.find(t => t.id === activeTab);

            return (
              <React.Fragment key={group.id}>
                {gi > 0 && <div className="h-5 w-px bg-gray-200 mx-1.5 flex-shrink-0" />}
                <button
                  onClick={() => {
                    setExpandedGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(group.id)) {
                        // Don't collapse if this group has the active tab
                        if (!groupHasActiveTab) next.delete(group.id);
                      } else {
                        next.add(group.id);
                      }
                      return next;
                    });
                  }}
                  className={`flex items-center gap-1 py-3 px-2 text-[10px] uppercase tracking-wider font-semibold flex-shrink-0 transition-colors rounded-t ${
                    groupHasActiveTab ? 'text-violet-600' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {group.label}
                  {!isExpanded && activeTabInGroup && (
                    <span className="ml-1 text-[10px] text-violet-500 normal-case tracking-normal font-medium">
                      / {activeTabInGroup.label}
                    </span>
                  )}
                </button>
                {isExpanded && group.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      // Auto-expand this group, collapse others
                      setExpandedGroups(new Set([group.id]));
                    }}
                    className={`flex items-center gap-1.5 py-3 px-2.5 border-b-2 font-medium text-xs whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-violet-500 text-violet-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {/* Merged Pages tab (web pages + landing pages) */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setPageTypeFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${pageTypeFilter === 'all' ? 'bg-violet-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>All Pages</button>
            <button onClick={() => setPageTypeFilter('page')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${pageTypeFilter === 'page' ? 'bg-violet-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>Web Pages</button>
            <button onClick={() => setPageTypeFilter('landing')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${pageTypeFilter === 'landing' ? 'bg-violet-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>Landing Pages</button>
          </div>
          <PagesTab
            pages={pageTypeFilter === 'all' ? [...pages, ...landingPages] : pageTypeFilter === 'page' ? pages : landingPages}
            onCreatePage={() => pageTypeFilter === 'landing' ? setCreateLandingPageModalOpen(true) : setCreatePageModalOpen(true)}
            onEditPage={(page) => { setSelectedPage(page); setEditPageModalOpen(true); }}
            onDesignPage={(page) => {
              // Unified builder is now THE design surface. Editing the site home
              // uses the public 'home' doc key; other pages use their own key.
              const s = sites.find((x) => x.id === activeSiteId);
              const bs = (s && (typeof s.brand_settings === 'string' ? JSON.parse(s.brand_settings || '{}') : s.brand_settings)) || {};
              // Treat the canonical home slugs as "home" too — home_page_id is
              // often unset, and the public apex reads the 'home' doc key, so
              // this keeps editing + deploying the home on one consistent key.
              const slug = (page.slug || '').replace(/^\/+|\/+$/g, '').toLowerCase();
              const isHome = bs.home_page_id === page.id || slug === 'home' || slug === 'index' || slug === '';
              window.location.href = `/design/v2?page=${encodeURIComponent(page.id)}${isHome ? '&home=1' : ''}`;
            }}
            onDeletePage={handleDeletePage}
            onPreview={handlePreviewPage}
            onDeploy={handleDeployPage}
            onRevert={handleRevertPage}
            homePageId={(() => {
              const s = sites.find((x) => x.id === activeSiteId);
              const bs = s && (typeof s.brand_settings === 'string' ? JSON.parse(s.brand_settings || '{}') : s.brand_settings) || {};
              return bs.home_page_id;
            })()}
            onSetHome={async (page) => {
              const s = sites.find((x) => x.id === activeSiteId);
              const bs = (s && (typeof s.brand_settings === 'string' ? JSON.parse(s.brand_settings || '{}') : s.brand_settings)) || {};
              const nextBrand = { ...bs, home_page_id: page.id };
              try {
                const res: any = await getPlatformApi().patch(`/web-presence/sites/${activeSiteId}`, { brand_settings: nextBrand });
                const updated = res && res.data ? res.data : { ...(s || {}), brand_settings: nextBrand };
                setSites((prev) => prev.map((x) => (x.id === activeSiteId ? updated : x)));
              } catch (e) {
                console.error('Failed to set public home page:', e);
              }
            }}
          />
        </div>
      )}

      {/* Posts & Social tab */}
      {activeTab === 'posts' && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Posts & Social Content</h3>
              <p className="text-sm text-gray-500">Manage AI-generated LinkedIn posts, social media content, and outreach copy.</p>
            </div>
            <button onClick={() => setActiveTab('ai')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Generate New Post
            </button>
          </div>
          <ContentOutputsTab contentTypes={['linkedin_post', 'social_post']} emptyIcon={<Share2 className="h-10 w-10 text-gray-300" />} emptyTitle="No posts yet" emptyDescription="Use AI Studio to generate LinkedIn and social media posts, then save them here for editing and publishing." />
        </div>
      )}

      {/* Emails tab */}
      {activeTab === 'emails' && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
              <p className="text-sm text-gray-500">Manage AI-generated email content for member outreach and campaigns.</p>
            </div>
            <button onClick={() => setActiveTab('ai')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Generate New Email
            </button>
          </div>
          <ContentOutputsTab contentTypes={['email']} emptyIcon={<Mail className="h-10 w-10 text-gray-300" />} emptyTitle="No emails yet" emptyDescription="Use AI Studio to generate email campaigns, then save them here for editing and sending." />
        </div>
      )}

      {/* Flyers & Print tab */}
      {activeTab === 'flyers' && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Flyers & Print</h3>
              <p className="text-sm text-gray-500">Manage AI-generated flyers, one-pagers, and print materials.</p>
            </div>
            <button onClick={() => setActiveTab('ai')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Generate New Flyer
            </button>
          </div>
          <ContentOutputsTab contentTypes={['flyer']} emptyIcon={<Printer className="h-10 w-10 text-gray-300" />} emptyTitle="No flyers yet" emptyDescription="Use AI Studio to generate flyers and print materials, then save them here for editing and exporting to PDF." />
        </div>
      )}
      {/* SMS tab */}
      {activeTab === 'sms' && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">SMS Messages</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage AI-generated SMS content for member outreach.</p>
            </div>
            <button onClick={() => setActiveTab('ai')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Generate SMS
            </button>
          </div>
          <ContentOutputsTab contentTypes={['sms']} emptyIcon={<MessageSquare className="h-10 w-10 text-gray-300" />} emptyTitle="No SMS messages yet" emptyDescription="Use AI Studio to generate SMS messages, then save them here for editing and sending." />
        </div>
      )}

      {/* Voice AI tab */}
      {activeTab === 'voice_ai' && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Voice AI Scripts</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage AI-generated voice call scripts for automated outreach.</p>
            </div>
            <button onClick={() => setActiveTab('ai')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Generate Script
            </button>
          </div>
          <ContentOutputsTab contentTypes={['voice_ai_script']} emptyIcon={<Mic className="h-10 w-10 text-gray-300" />} emptyTitle="No voice scripts yet" emptyDescription="Use AI Studio to generate voice AI scripts, then save them here for use in automated calls." />
        </div>
      )}

      {activeTab === 'forms' && (
        <FormsTab
          forms={forms}
          onCreateForm={() => { setEditingForm(null); setCreateFormModalOpen(true); }}
          onEditForm={(f) => { setEditingForm(f); setCreateFormModalOpen(true); }}
        />
      )}
      {activeTab === 'assets' && (
        <AssetsTab
          assets={assets}
          onUploadAsset={() => setUploadAssetModalOpen(true)}
          onViewAsset={(asset) => {
            setSelectedAsset(asset);
            setViewAssetModalOpen(true);
          }}
          onEditAsset={(asset) => {
            setSelectedAsset(asset);
            setEditAssetModalOpen(true);
          }}
          onDeleteAsset={handleDeleteAsset}
        />
      )}
      {activeTab === 'seo' && (
        <div className="space-y-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSeoSubTab('overview')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${seoSubTab === 'overview' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
            >
              SEO Settings
            </button>
            <button
              onClick={() => setSeoSubTab('bulk')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${seoSubTab === 'bulk' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
            >
              Bulk Editor
            </button>
            <button
              onClick={() => setSeoSubTab('redirects')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${seoSubTab === 'redirects' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
            >
              Redirects
            </button>
          </div>
          {seoSubTab === 'overview' && <SEOTab />}
          {seoSubTab === 'bulk' && (
            <div className="bg-white rounded-xl border shadow-sm">
              <BulkSEOEditor
                pages={pages.map(p => ({ id: p.id, title: p.title, slug: p.slug, metaTitle: p.title, metaDescription: '', noIndex: false }))}
                onSave={async (updates) => {
                  console.log('Bulk SEO save:', updates);
                }}
              />
            </div>
          )}
          {seoSubTab === 'redirects' && (
            <div className="bg-white rounded-xl border shadow-sm">
              <RedirectManager
                redirects={redirects}
                onSave={async (redirect) => {
                  if (redirect.id) {
                    await webPresenceApi.updateRedirect(redirect.id, redirect);
                  } else {
                    await webPresenceApi.createRedirect({ ...redirect, site_id: activeSiteId });
                  }
                  if (activeSiteId) {
                    const res = await webPresenceApi.getRedirects(activeSiteId);
                    if (res.success && res.data) setRedirects(res.data);
                  }
                }}
                onDelete={async (redirectId) => {
                  await webPresenceApi.deleteRedirect(redirectId);
                  setRedirects(prev => prev.filter(r => r.id !== redirectId));
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Content Types Tab */}
      {activeTab === 'content' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <ContentTypeManager
            onApiCall={async (method: string, path: string, body?: any) => {
              const api = webPresenceApi as any;
              const fullPath = `/web-presence${path}`;
              if (method === 'GET') {
                const res = getPlatformApi().get(fullPath);
                return res.data;
              } else if (method === 'POST') {
                const res = getPlatformApi().post(fullPath, body);
                return res.data;
              } else if (method === 'PATCH') {
                const res = getPlatformApi().patch(fullPath, body);
                return res.data;
              } else if (method === 'DELETE') {
                const res = getPlatformApi().delete(fullPath);
                return res.data;
              }
            }}
          />
        </div>
      )}

      {/* Design Tab */}
      {activeTab === 'design' && (
        <div className="space-y-4">
          {/* Design System sub-tabs */}
          <div className="flex items-center gap-2 mb-4">
            {(['brand', 'tokens', 'templates', 'import'] as const).map((tab) => {
              const labels = { brand: 'Brand & Logos', tokens: 'Design Tokens', templates: 'Section Templates', import: 'Import / Reference' };
              return (
                <button key={tab} onClick={() => setDesignSubTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${designSubTab === tab ? 'bg-violet-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
                >{labels[tab]}</button>
              );
            })}
            {sites.length > 1 && (
              <select value={activeSiteId} onChange={(e) => setActiveSiteId(e.target.value)} className="ml-auto px-3 py-2 border rounded-lg text-sm">
                {sites.map((site: any) => (<option key={site.id} value={site.id}>{site.site_name || site.siteName}</option>))}
              </select>
            )}
          </div>

          {/* Brand & Logos — aggregated brand identity view */}
          {designSubTab === 'brand' && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Brand Identity</h3>
              <p className="text-sm text-gray-500 mb-6">Your brand assets, logos, and color palette — used by AI Studio when generating content.</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Color Palette from tokens */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Palette className="h-4 w-4" /> Color Palette</h4>
                  <div className="flex flex-wrap gap-2">
                    {designTokens.filter((t: any) => (t.token_group || t.group) === 'color').length > 0
                      ? designTokens.filter((t: any) => (t.token_group || t.group) === 'color').map((t: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border">
                          <div className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: t.token_value || t.value }} />
                          <div>
                            <div className="text-xs font-medium text-gray-700">{t.token_name || t.name}</div>
                            <div className="text-xs text-gray-400">{t.token_value || t.value}</div>
                          </div>
                        </div>
                      ))
                      : <p className="text-sm text-gray-400">No color tokens defined. Add them in Design Tokens tab.</p>
                    }
                  </div>
                </div>

                {/* Typography from tokens */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Type className="h-4 w-4" /> Typography</h4>
                  <div className="space-y-2">
                    {designTokens.filter((t: any) => (t.token_group || t.group) === 'typography').length > 0
                      ? designTokens.filter((t: any) => (t.token_group || t.group) === 'typography').map((t: any, i: number) => (
                        <div key={i} className="px-3 py-2 bg-gray-50 rounded-lg border">
                          <div className="text-xs font-medium text-gray-700">{t.token_name || t.name}</div>
                          <div className="text-xs text-gray-400">{t.token_value || t.value}</div>
                        </div>
                      ))
                      : <p className="text-sm text-gray-400">No typography tokens defined.</p>
                    }
                  </div>
                </div>

                {/* Logos & Brand Assets */}
                <div className="lg:col-span-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Image className="h-4 w-4" /> Logos & Brand Assets</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {assets.filter(a => a.type === 'image').slice(0, 8).map((asset) => (
                      <div key={asset.id} className="p-2 bg-gray-50 rounded-lg border hover:border-violet-300 cursor-pointer transition-colors">
                        <div className="aspect-video bg-white rounded flex items-center justify-center overflow-hidden mb-1">
                          <Image className="h-8 w-8 text-gray-300" />
                        </div>
                        <div className="text-xs font-medium text-gray-600 truncate">{asset.name}</div>
                      </div>
                    ))}
                    {assets.filter(a => a.type === 'image').length === 0 && (
                      <p className="text-sm text-gray-400 col-span-4">No image assets uploaded. Go to Assets & Media tab to upload logos and images.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-violet-50 rounded-lg border border-violet-200">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  <span className="text-sm font-medium text-violet-800">AI Integration</span>
                </div>
                <p className="text-xs text-violet-600">
                  Your brand colors, typography, and assets are automatically injected into AI Studio prompts
                  so generated content matches your brand identity.
                </p>
              </div>

              <BrandTokenGenerator
                siteId={activeSiteId}
                onApplied={() =>
                  webPresenceApi.getDesignTokens(activeSiteId).then((r) => {
                    if (r.success && r.data) setDesignTokens(r.data);
                  })
                }
              />
            </div>
          )}

          {/* Design Tokens */}
          {designSubTab === 'tokens' && (
            <div className="bg-white rounded-xl border shadow-sm">
              <DesignTokenEditor
                tokens={designTokens}
                siteId={activeSiteId}
                onChange={setDesignTokens}
                onSave={async (token) => {
                  if (token.id) {
                    await webPresenceApi.updateDesignToken(token.id, token);
                  } else {
                    const res = await webPresenceApi.createDesignToken({ ...token, site_id: activeSiteId });
                    if (res.success && res.data) {
                      setDesignTokens(prev => [...prev, res.data!]);
                    }
                  }
                }}
                onDelete={async (tokenId) => {
                  await webPresenceApi.deleteDesignToken(tokenId);
                  setDesignTokens(prev => prev.filter(t => t.id !== tokenId));
                }}
              />
            </div>
          )}

          {/* Section Templates */}
          {designSubTab === 'templates' && (
            <div className="bg-white rounded-xl border shadow-sm">
              <SectionTemplateLibrary
                templates={[]}
                onApply={() => {}}
                onSave={async () => {}}
                onDelete={async () => {}}
              />
            </div>
          )}

          {/* Import / Reference External Sites */}
          {designSubTab === 'import' && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Import & Reference</h3>
              <p className="text-sm text-gray-500 mb-6">Reference existing websites, import HTML content, or connect external design systems.</p>

              <div className="space-y-4">
                {/* URL Reference */}
                <ReferenceSiteAnalyzer
                  siteId={activeSiteId}
                  onApplied={() =>
                    webPresenceApi.getDesignTokens(activeSiteId).then((r) => {
                      if (r.success && r.data) setDesignTokens(r.data);
                    })
                  }
                />

                {/* HTML Import */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><Code2 className="h-4 w-4" /> Import HTML Content</h4>
                  <p className="text-xs text-gray-500 mb-3">Paste HTML from an existing page to convert it into editable content blocks.</p>
                  <textarea placeholder="Paste your HTML here..." rows={4} className="w-full px-3 py-2 text-sm border rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none" />
                  <button className="mt-2 px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 flex items-center gap-2">
                    <Upload className="h-4 w-4" /> Convert to Blocks
                  </button>
                </div>

                {/* Connected Sites */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><Layout className="h-4 w-4" /> Connected Sites</h4>
                  <p className="text-xs text-gray-500 mb-3">Sites in your Design Studio that AI can reference when generating new content.</p>
                  {sites.length > 0 ? (
                    <div className="space-y-2">
                      {sites.map((site: any) => (
                        <div key={site.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            <Globe className="h-4 w-4 text-blue-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-700">{site.site_name || site.siteName}</div>
                              <div className="text-xs text-gray-400">{site.subdomain || site.custom_domain || 'No domain set'}</div>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${site.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {site.status || 'draft'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No sites configured yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Studio Tab */}
      {activeTab === 'ai' && !isCommsMode && (
        <DesignStudioDock siteId={activeSiteId} onPageBuilt={reloadPages} />
      )}

      {activeTab === 'ai' && isCommsMode && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="flex h-[calc(100vh-280px)]">
            <AIDesignAssistant
              siteId={activeSiteId}
              isOpen={true}
              mode={isCommsMode ? 'comms' : 'web'}
              initialPrompt={inboundPrompt || undefined}
              initialContentType={inboundPrompt ? 'landing_page' : undefined}
              onClose={() => setActiveTab(isCommsMode ? 'emails' : 'pages')}
              onPreview={(blocks) => setAiPreviewBlocks(normalizeAIBlocks(blocks))}
              onApplyBlocks={(blocks) => {
                // Normalize AI blocks to ContentBlock format
                const normalized = normalizeAIBlocks(blocks);
                setAiPreviewBlocks(normalized);
                const createAndEdit = async () => {
                  try {
                    const res = await webPresenceApi.createPage({
                      title: 'AI Generated Page',
                      slug: `ai-page-${Date.now()}`,
                      page_type: 'page',
                      site_id: activeSiteId,
                      status: 'draft',
                      content_blocks: normalized,
                    } as any);
                    if (res.success && res.data) {
                      // Inject normalized blocks into raw data so editor gets them
                      res.data.content_blocks = normalized;
                      const newPage = mapApiPage(res.data);
                      setPages(prev => [...prev, newPage]);
                      // Open the unified builder (seeds from these AI blocks).
                      window.location.href = `/design/v2?page=${encodeURIComponent(newPage.id)}`;
                    }
                  } catch (err) {
                    console.error('Failed to create page from AI output:', err);
                  }
                };
                createAndEdit();
              }}
              onSaveAsOutput={(gen, navigateTo) => {
                // Content is already saved as an AI generation — navigate to the output tab
                setActiveTab(navigateTo as WebPresenceTab);
              }}
            />
            {/* Right side: live preview of generated content */}
            <div className="flex-1 border-l bg-gray-50 overflow-auto">
              {aiPreviewBlocks.length > 0 ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-700">Page Preview</h4>
                    <span className="text-xs text-gray-400">{aiPreviewBlocks.length} blocks</span>
                  </div>
                  <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {aiPreviewBlocks.map((block: any, idx: number) => (
                      <AIBlockPreview key={block.id || idx} block={block} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Sparkles size={48} className="text-violet-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">AI Design Studio</h3>
                  <p className="text-sm text-gray-500 max-w-md mb-6">
                    Generate web pages, LinkedIn posts, flyers, and email campaigns using AI.
                    Select a content type, describe what you need, and click "Generate" — your live preview appears here.
                  </p>
                  <div className="grid grid-cols-2 gap-3 max-w-sm text-left">
                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                      <Globe size={16} className="text-blue-500 flex-shrink-0" />
                      <div><div className="text-xs font-medium text-gray-700">Web Pages</div><div className="text-xs text-gray-400">Full page layouts</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                      <Sparkles size={16} className="text-violet-500 flex-shrink-0" />
                      <div><div className="text-xs font-medium text-gray-700">Landing Pages</div><div className="text-xs text-gray-400">Conversion-focused</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                      <FileText size={16} className="text-orange-500 flex-shrink-0" />
                      <div><div className="text-xs font-medium text-gray-700">Posts & Flyers</div><div className="text-xs text-gray-400">LinkedIn, social, print</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                      <Globe size={16} className="text-green-500 flex-shrink-0" />
                      <div><div className="text-xs font-medium text-gray-700">Email Campaigns</div><div className="text-xs text-gray-400">Member outreach</div></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Code Tab */}
      {activeTab === 'code' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Code Snippets</h3>
            <p className="text-sm text-gray-500 mb-4">Manage reusable code snippets. Use the Page Editor to add Custom Code blocks with live preview.</p>
          </div>
          <SnippetLibrary
            snippets={codeSnippets}
            onInsert={(snippet) => {
              navigator.clipboard.writeText(snippet.code || '');
            }}
            onSave={async (snippet) => {
              const res = await webPresenceApi.createCodeSnippet(snippet);
              if (res.success && res.data) {
                setCodeSnippets(prev => [...prev, res.data!]);
              }
            }}
            onDelete={async (snippetId) => {
              await webPresenceApi.deleteCodeSnippet(snippetId);
              setCodeSnippets(prev => prev.filter(s => s.id !== snippetId));
            }}
          />
        </div>
      )}

      {/* Team/Permissions Tab */}
      {activeTab === 'team' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <PermissionManager
            teamMembers={teamMembers}
            roleMappings={roleMappings}
            permissions={permissions}
            onSaveOverride={async (userId, permission) => {
              const payload = {
                user_id: userId,
                role: permission.role || 'viewer',
                can_edit_design: permission.canEditDesign || false,
                can_edit_content: permission.canEditContent || false,
                can_publish: permission.canPublish || false,
                can_delete: permission.canDelete || false,
                can_manage_seo: permission.canManageSeo || false,
                can_manage_forms: permission.canManageForms || false,
                can_manage_assets: permission.canManageAssets || false,
                can_use_code_editor: permission.canUseCodeEditor || false,
                can_manage_content_types: permission.canManageContentTypes || false,
              };
              await webPresenceApi.savePermission(payload);
              // Refresh team members to show updated data
              const tmRes = await webPresenceApi.getTeamMembers();
              if (tmRes.success && tmRes.data) setTeamMembers(tmRes.data);
            }}
            onRemoveOverride={async (userId) => {
              // Delete the override by setting to viewer (soft delete)
              try {
                await webPresenceApi.updatePermission(userId, { role: 'viewer' });
              } catch { /* override may not exist */ }
              // Refresh team members
              const tmRes = await webPresenceApi.getTeamMembers();
              if (tmRes.success && tmRes.data) setTeamMembers(tmRes.data);
            }}
            onUpdateRoleMapping={async (systemRole, updates) => {
              await webPresenceApi.updateRoleMapping(systemRole, updates);
              // Refresh both role mappings and team members
              const [rmRes, tmRes] = await Promise.all([
                webPresenceApi.getRoleMappings(),
                webPresenceApi.getTeamMembers(),
              ]);
              if (rmRes.success && rmRes.data) setRoleMappings(rmRes.data);
              if (tmRes.success && tmRes.data) setTeamMembers(tmRes.data);
            }}
          />
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <ActivityLog
            entries={activityEntries}
            onLoadMore={async () => {
              const res = await webPresenceApi.getActivityLog({ offset: activityEntries.length, limit: 20 });
              if (res.success && res.data) {
                setActivityEntries(prev => [...prev, ...res.data!]);
              }
            }}
            hasMore={true}
          />
        </div>
      )}

      {/* Modals */}
      <CreatePageModal
        isOpen={createPageModalOpen}
        onClose={() => setCreatePageModalOpen(false)}
        onSubmit={handleCreatePage}
        type="page"
      />
      <CreatePageModal
        isOpen={createLandingPageModalOpen}
        onClose={() => setCreateLandingPageModalOpen(false)}
        onSubmit={handleCreateLandingPage}
        type="landing"
      />
      <FormEditorModal
        isOpen={createFormModalOpen}
        onClose={() => { setCreateFormModalOpen(false); setEditingForm(null); }}
        form={editingForm}
        onSubmit={(data) => { if (editingForm) handleUpdateForm(editingForm.id, data); else handleCreateForm(data); }}
      />
      <UploadAssetModal
        isOpen={uploadAssetModalOpen}
        onClose={() => setUploadAssetModalOpen(false)}
        onSubmit={handleUploadAsset}
      />
      <EditPageModal
        isOpen={editPageModalOpen}
        onClose={() => {
          setEditPageModalOpen(false);
          setSelectedPage(null);
        }}
        page={selectedPage}
        onSubmit={handleEditPage}
      />
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={itemToDelete?.onConfirm || (() => {})}
        itemName={itemToDelete?.name || ''}
        itemType={itemToDelete?.type || ''}
      />
      {/* Full-screen Page Editor - Portal to body to escape sidebar stacking context */}
      {pageEditorOpen && selectedPageRaw && createPortal(
        <div className="fixed inset-0 z-[9999] bg-white">
          <PageEditor
            page={mapApiPageToEditorPage(selectedPageRaw)}
            onSave={async (updatedPage) => {
              try {
                await webPresenceApi.updatePage(updatedPage.id, {
                  title: updatedPage.pageTitle,
                  slug: updatedPage.pageSlug,
                  content_blocks: updatedPage.contentBlocks,
                  meta_title: updatedPage.metaTitle,
                  meta_description: updatedPage.metaDescription,
                } as any);
                // Refresh pages list
                if (selectedPage) {
                  const updated = { ...selectedPage, title: updatedPage.pageTitle, slug: updatedPage.pageSlug, lastModified: new Date().toISOString().split('T')[0] };
                  if (activeTab === 'pages') {
                    setPages(prev => prev.map(p => p.id === updated.id ? updated : p));
                  } else {
                    setLandingPages(prev => prev.map(p => p.id === updated.id ? updated : p));
                  }
                }
              } catch (err) {
                console.error('Failed to save page:', err);
              }
            }}
            onPublish={async () => {
              try {
                await getPlatformApi().post(`/web-presence/pages/${selectedPageRaw.id}/publish`, {});
                if (selectedPage) {
                  const updated = { ...selectedPage, status: 'published' as const };
                  setPages(prev => prev.map(p => p.id === updated.id ? updated : p));
                }
              } catch (err) {
                console.error('Failed to publish page:', err);
              }
            }}
            onPreview={() => {}}
            onClose={() => {
              setPageEditorOpen(false);
              setSelectedPage(null);
              setSelectedPageRaw(null);
            }}
          />
        </div>,
        document.body
      )}
      <ViewAssetModal
        isOpen={viewAssetModalOpen}
        onClose={() => {
          setViewAssetModalOpen(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
      />
      <EditAssetModal
        isOpen={editAssetModalOpen}
        onClose={() => {
          setEditAssetModalOpen(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
        onSave={(updatedAsset) => {
          setAssets(assets.map(a => a.id === updatedAsset.id ? updatedAsset : a));
        }}
      />

      {/* Full-screen PUBLIC preview — renders the page exactly as it would
          appear live (same PublicBlockRenderer the public site uses), WITHOUT
          publishing. Drafts stay private; this is local-only. */}
      {/* Portal to <body> so the preview covers the WHOLE viewport — including
          the app sidebar/rail. Rendered inline it sits inside the app shell
          (transformed/stacked ancestors) and the rail bled through. The public
          site has no app chrome, so neither should the preview. */}
      {previewPage && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 2147483000, background: '#ffffff', overflow: 'auto' }}
        >
          <div
            style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 2147483001 }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/80 text-white text-xs font-medium shadow-lg backdrop-blur"
          >
            <Eye className="h-3.5 w-3.5" />
            Preview — not deployed
            <button
              className="ml-2 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30"
              onClick={() => setPreviewPage(null)}
            >
              Close
            </button>
          </div>
          <PublicBlockRenderer
            page={previewPage}
            loginUrl={typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login'}
            loginLabel="Log in"
            signupUrl={typeof window !== 'undefined' ? `${window.location.origin}/signup` : '/signup'}
          />
        </div>,
        document.body,
      )}
      </div>
    </div>
  );
}

// ============================================================================
// CONTENT OUTPUTS TAB — reusable list for posts, emails, flyers
// ============================================================================

function ContentOutputsTab({ contentTypes, emptyIcon, emptyTitle, emptyDescription }: {
  contentTypes: string[];
  emptyIcon: React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const [outputs, setOutputs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Load from AI generations history filtered by content type
        const promises = contentTypes.map(ct =>
          webPresenceApi.aiGetGenerations({ content_type: ct, limit: 50 })
        );
        const results = await Promise.all(promises);
        const all = results.flatMap(r => (r.success && Array.isArray(r.data) ? r.data : []));
        setOutputs(all.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  if (outputs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        {emptyIcon}
        <h4 className="text-sm font-medium text-gray-700 mt-3">{emptyTitle}</h4>
        <p className="text-xs text-gray-400 mt-1 max-w-sm">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {outputs.map((item: any) => {
        const isBlocks = item.content_type === 'flyer';
        const copyText = item.generated_copy || '';
        const preview = copyText.length > 200 ? copyText.slice(0, 200) + '...' : copyText;

        return (
          <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg hover:border-violet-300 hover:bg-violet-50/30 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500 capitalize">{(item.content_type || '').replace('_', ' ')}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  item.status === 'applied' ? 'bg-green-100 text-green-700'
                    : item.status === 'completed' ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>{item.status}</span>
                <span className="text-xs text-gray-400 ml-auto">{new Date(item.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-gray-700 line-clamp-1 font-medium mb-1">{item.prompt}</p>
              {!isBlocks && preview && (
                <p className="text-xs text-gray-500 line-clamp-2">{preview}</p>
              )}
              {isBlocks && (
                <p className="text-xs text-gray-500">{JSON.parse(item.generated_blocks || '[]').length} content blocks</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => {
                  const text = item.generated_copy || JSON.stringify(item.generated_blocks, null, 2);
                  navigator.clipboard.writeText(text);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Copy"
              >
                <Copy className="h-4 w-4" />
              </button>
              {item.content_type === 'linkedin_post' && (
                <button
                  onClick={() => window.open('https://www.linkedin.com/feed/', '_blank')}
                  className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  title="Open LinkedIn"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// AI BLOCK PREVIEW — renders a visual preview of generated content blocks
// ============================================================================

function AIBlockPreview({ block }: { block: any }) {
  const content = block.content || {};
  const styles = block.styles || {};
  const type = block.type || block.blockType || 'text';

  const bgColor = styles.backgroundColor || '#ffffff';
  const textColor = styles.textColor || '#1f2937';
  const padding = styles.padding || '32px 24px';

  switch (type) {
    case 'hero':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding, textAlign: 'center' as const }}>
          <h1 className="text-2xl font-bold mb-2">{content.headline || content.title || 'Hero Section'}</h1>
          {content.subheadline && <p className="text-base opacity-80 mb-4">{content.subheadline}</p>}
          {content.ctaButtons?.[0] && (
            <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
              {content.ctaButtons[0].text || 'Get Started'}
            </span>
          )}
          {content.buttonText && (
            <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
              {content.buttonText}
            </span>
          )}
        </div>
      );

    case 'features':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding }}>
          {content.headline && <h2 className="text-xl font-bold mb-4 text-center">{content.headline}</h2>}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {(content.features || content.items || []).map((f: any, i: number) => (
              <div key={i} className="p-3 rounded-lg bg-white/50 border border-gray-100">
                {f.icon && <div className="text-xl mb-1">{f.icon}</div>}
                <h4 className="text-sm font-semibold">{f.title || f.name}</h4>
                <p className="text-xs opacity-70 mt-1">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'cta':
      return (
        <div style={{ backgroundColor: bgColor || '#3b82f6', color: textColor || '#ffffff', padding, textAlign: 'center' as const }}>
          <h2 className="text-xl font-bold mb-2">{content.headline || content.title || 'Call to Action'}</h2>
          {content.description && <p className="text-sm opacity-80 mb-3">{content.description}</p>}
          <span className="inline-block px-5 py-2 bg-white text-blue-600 rounded-lg text-sm font-semibold">
            {content.buttonText || content.ctaText || 'Learn More'}
          </span>
        </div>
      );

    case 'stats':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding }}>
          {content.headline && <h2 className="text-lg font-bold mb-3 text-center">{content.headline}</h2>}
          <div className="flex justify-center gap-8">
            {(content.stats || content.items || []).map((s: any, i: number) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold">{s.value || s.number}</div>
                <div className="text-xs opacity-70">{s.label || s.title}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'testimonials':
      return (
        <div style={{ backgroundColor: bgColor || '#f8fafc', color: textColor, padding }}>
          {content.headline && <h2 className="text-lg font-bold mb-3 text-center">{content.headline}</h2>}
          {(content.testimonials || content.items || []).map((t: any, i: number) => (
            <div key={i} className="p-3 bg-white rounded-lg border mb-2">
              <p className="text-sm italic mb-2">"{t.quote || t.text}"</p>
              <p className="text-xs font-medium">{t.author || t.name}{t.role ? `, ${t.role}` : ''}</p>
            </div>
          ))}
        </div>
      );

    case 'faq':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding }}>
          <h2 className="text-lg font-bold mb-3 text-center">{content.headline || 'FAQ'}</h2>
          {(content.questions || content.items || content.faqs || []).map((q: any, i: number) => (
            <div key={i} className="mb-3 border-b pb-2">
              <h4 className="text-sm font-semibold">{q.question || q.title}</h4>
              <p className="text-xs opacity-70 mt-1">{q.answer || q.description}</p>
            </div>
          ))}
        </div>
      );

    case 'text':
    case 'richtext':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding }}>
          {content.headline && <h2 className="text-lg font-bold mb-2">{content.headline}</h2>}
          <p className="text-sm">{content.text || content.richText || content.body || ''}</p>
        </div>
      );

    case 'form':
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding, textAlign: 'center' as const }}>
          <h2 className="text-lg font-bold mb-2">{content.headline || content.title || 'Contact Form'}</h2>
          {content.description && <p className="text-sm opacity-70 mb-3">{content.description}</p>}
          <div className="max-w-xs mx-auto space-y-2">
            <div className="h-8 bg-gray-100 rounded border" />
            <div className="h-8 bg-gray-100 rounded border" />
            <div className="h-16 bg-gray-100 rounded border" />
            <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium">
              {content.buttonText || 'Submit'}
            </span>
          </div>
        </div>
      );

    default:
      return (
        <div style={{ backgroundColor: bgColor, color: textColor, padding }}>
          <div className="text-xs font-medium text-gray-400 mb-1 uppercase">{type}</div>
          {content.headline && <h2 className="text-lg font-bold mb-1">{content.headline}</h2>}
          {content.title && <h3 className="text-base font-semibold mb-1">{content.title}</h3>}
          {content.description && <p className="text-sm opacity-70">{content.description}</p>}
          {content.text && <p className="text-sm">{content.text}</p>}
        </div>
      );
  }
}

export default WebPresencePage;
